import * as React from 'react';
import { connect } from 'react-redux';
import {
    compileAdvancedFilter,
    getAdvancedFilterExtraDepth,
    validateAdvancedFilter,
} from '@/domain/advancedFilter';
import { Vegobjekttype } from '@/domain/vegobjekter/Vegobjekttype';
import {
    VegobjekttypeCategoryState,
    VegobjekttypeState,
    VegobjekttypeStateError,
} from '@/state';
import { ColorIndex, colorsEqual } from '@/utils/ColorHelper';
import { getDefaultCategoryColor } from '@/utils/ColorPreferences';
import Accordion from '../../objects/Accordion';
import { Loading } from '../../objects/Loading';
import { Categorize } from './categorize/Categorize';
import { CategoryResultList } from './categorize/CategoryResultList';
import { DownloadResultsBox } from './DownloadResultsBox';
import { HvaInfo } from './HvaInfo';
import { RoadRefOrAreaSummary } from './RoadRefOrAreaSummary';
import {
    clearAdvancedFilter,
    removeVegobjekttype,
    resetVegobjekttypeCategoryColor,
    setVegobjekttypeCategoryColor,
} from '@/actions/vegobjekterActions';
import {
    HitStatistics,
    AllCategoriesHitStatistics,
    AdvancedFilterWarningIcon,
} from './HitStatistics';
import { AdvancedFilterBuilder } from './filter/advanced/AdvancedFilterBuilder';
import { AdvancedFilterTutorial } from './filter/advanced/AdvancedFilterTutorial';
import InfoBox from '../../common/InfoBox';
import { ColorMenu, ColorMenuAction } from './ColorMenu';
import '../../../stylesheets/5_components/limiter/what/categorize/Categorize.less';
import '../../../stylesheets/5_components/limiter/what/filter/Filters.less';
import '../../../stylesheets/5_components/limiter/what/filter/AdvancedFilter.less';

import { ConfigAndDataContext } from '@/state/context/ConfigAndDataContext';
import { getApiURLVegobjekter } from '@/utils/Utils';

interface TagProps {
    vegobjekttype: Vegobjekttype;
    vegobjekttypeData: VegobjekttypeState;
    farge: ColorIndex;
    isLoggedIn: boolean;
}

interface DispatchProps {
    doClearAdvancedFilter: (vegobjekttypeData: VegobjekttypeState) => void;
    doRemoveRoadObjectType: (vegobjekttypeData: VegobjekttypeState) => void;
    doSetVegobjekttypeCategoryColor: (
        vegobjekttypeData: VegobjekttypeState,
        categoryState: VegobjekttypeCategoryState,
        color: ColorIndex,
        saveAsDefault?: boolean
    ) => void;
    doResetVegobjekttypeCategoryColor: (
        vegobjekttypeData: VegobjekttypeState,
        categoryState: VegobjekttypeCategoryState
    ) => void;
}

type Props = TagProps & DispatchProps;

interface State {
    showCategoryResults: boolean;
    isAdvancedFilterOpen: boolean;
    activeColorCategory: VegobjekttypeCategoryState | null;
    categoryColorMenuAnchor: { x: number; y: number };
    isCategoryColorMenuOpen: boolean;
}

type WhatListElementStyle = React.CSSProperties & {
    '--c-what-list-element-extra-depth'?: string;
};

const mapDispatchToProps = {
    doClearAdvancedFilter: clearAdvancedFilter,
    doRemoveRoadObjectType: removeVegobjekttype,
    doSetVegobjekttypeCategoryColor: setVegobjekttypeCategoryColor,
    doResetVegobjekttypeCategoryColor: resetVegobjekttypeCategoryColor,
};

class VegobjekttypeContainerComponent extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            showCategoryResults: true,
            isAdvancedFilterOpen: false,
            activeColorCategory: null,
            categoryColorMenuAnchor: { x: 0, y: 0 },
            isCategoryColorMenuOpen: false,
        };
    }

    static contextType = ConfigAndDataContext;
    declare context: React.ContextType<typeof ConfigAndDataContext>;

    openAdvancedFilter = () => {
        this.setState({ isAdvancedFilterOpen: true });
    };

    clearAdvancedFilter = () => {
        this.props.doClearAdvancedFilter(this.props.vegobjekttypeData);
        this.setState({ isAdvancedFilterOpen: false });
    };

    openCategoryColorMenu = (
        categoryState: VegobjekttypeCategoryState,
        anchor: { x: number; y: number }
    ) => {
        this.setState({
            activeColorCategory: categoryState,
            categoryColorMenuAnchor: anchor,
            isCategoryColorMenuOpen: true,
        });
    };

    closeCategoryColorMenu = () => {
        this.setState({ isCategoryColorMenuOpen: false });
    };

    getCategoryColorActions = (
        vegobjekttypeData: VegobjekttypeState,
        category: VegobjekttypeCategoryState
    ): ColorMenuAction[] => {
        const savedDefaultColor =
            vegobjekttypeData.typeId === null
                ? null
                : getDefaultCategoryColor(vegobjekttypeData.typeId, category.filters);
        const actions: ColorMenuAction[] = [
            {
                title: `Bruk farge for ${category.toString()}`,
                disabledTitle: `Fargen er allerede valgt for ${category.toString()}`,
                icon: (
                    <i className="material-icons" aria-hidden="true">
                        check
                    </i>
                ),
                className: 'c-color-menu__action--primary',
                onClick: color =>
                    this.props.doSetVegobjekttypeCategoryColor(
                        vegobjekttypeData,
                        category,
                        color
                    ),
                disabled: color => colorsEqual(color, category.color),
            },
            {
                title: `Bruk som standardfarge for ${category.toString()}`,
                disabledTitle: `Fargen er allerede standardfarge for ${category.toString()}`,
                icon: (
                    <i className="material-icons" aria-hidden="true">
                        star
                    </i>
                ),
                onClick: color =>
                    this.props.doSetVegobjekttypeCategoryColor(
                        vegobjekttypeData,
                        category,
                        color,
                        true
                    ),
                disabled: color => colorsEqual(color, savedDefaultColor),
            },
        ];

        if (category.colorIsManual || savedDefaultColor !== null) {
            actions.push({
                title: `Tilbakestill ${category.toString()} til automatisk farge`,
                icon: (
                    <i className="material-icons" aria-hidden="true">
                        refresh
                    </i>
                ),
                onClick: () =>
                    this.props.doResetVegobjekttypeCategoryColor(
                        vegobjekttypeData,
                        category
                    ),
            });
        }

        return actions;
    };

    render() {
        const { vegobjekttypeData, vegobjekttype, farge } = this.props;
        const category = vegobjekttypeData.category;
        const categoryStates = vegobjekttypeData.categoryStates;
        const { result } = vegobjekttypeData;
        const hasCategoryResults =
            categoryStates &&
            categoryStates.length > 0 &&
            category &&
            category.egenskapstype !== null;

        const config = this.context?.config;

        if (!config) {
            return null;
        }

        const vegobjekt_base_url = getApiURLVegobjekter(config);
        const vegobjekt_href =
            vegobjekt_base_url + '/vegobjekter/' + vegobjekttypeData.typeId;
        const advancedFilterDraft = vegobjekttypeData.getAdvancedFilterDraft();
        const advancedFilterApplied = vegobjekttypeData.getAdvancedFilterApplied();
        const advancedFilterRoot = advancedFilterDraft ?? advancedFilterApplied;
        const showAdvancedFilterWarning =
            !validateAdvancedFilter(advancedFilterDraft).valid &&
            Boolean(compileAdvancedFilter(advancedFilterApplied));
        const hasAdvancedFilters = advancedFilterRoot !== null;
        const isAdvancedFilterOpen =
            this.state.isAdvancedFilterOpen || hasAdvancedFilters;
        const filterToggleLabel = isAdvancedFilterOpen
            ? 'Fjern alle filtere'
            : 'Legg til filter';
        const filterToggleIcon = isAdvancedFilterOpen ? 'clear' : 'add';
        const advancedFilterExtraDepth = getAdvancedFilterExtraDepth(advancedFilterRoot);
        const activeColorCategory = this.state.activeColorCategory;
        const elementStyle: WhatListElementStyle = {
            '--c-what-list-element-extra-depth': advancedFilterExtraDepth.toString(),
        };

        if (!vegobjekttype) {
            return (
                <li
                    tabIndex={-1}
                    key={vegobjekttypeData.typeId}
                    className="c-what-list__element"
                    style={elementStyle}
                    aria-label="Vegobjekttype ikke funnet"
                >
                    <div className="c-hva-info c-hva-info--missing">
                        <div className="c-hva-info__leading">
                            <span className="c-hva-info__status-icon" aria-hidden="true">
                                <i className="icon-exclamation-triangle c-what-error__not-found" />
                            </span>
                        </div>
                        <div className="c-hva-info__content c-hva-info__content--missing">
                            <p className="c-hva-info__title">
                                Finner ikke vegobjekt med typeId{' '}
                                <a
                                    href={vegobjekt_href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {vegobjekttypeData.typeId}
                                </a>
                                .
                            </p>
                        </div>

                        <button
                            className="c-hva-info__remove-button"
                            type="button"
                            onClick={() => {
                                this.props.doRemoveRoadObjectType(vegobjekttypeData);
                            }}
                            aria-label={`Fjern vegobjekttype ${vegobjekttypeData.typeId} fra søk`}
                        >
                            <i className="o-icon-remove c-hva-info__remove-icon" />
                        </button>
                    </div>
                </li>
            );
        }

        return (
            <>
                <li
                    tabIndex={-1}
                    key={this.props.vegobjekttypeData.typeId}
                    className={'c-what-list__element'}
                    style={elementStyle}
                    onKeyDown={e => {
                        if (
                            e.key === 'Backspace' &&
                            (e.target as HTMLElement).classList.contains(
                                'c-hva-list-element'
                            )
                        ) {
                            this.props.doRemoveRoadObjectType(vegobjekttypeData);
                        }
                    }}
                    aria-label={`informasjon om søk etter ${vegobjekttype.navn}`}
                >
                    <div className={'c-hva-info'}>
                        <HvaInfo
                            vegobjekttypeData={vegobjekttypeData}
                            vegobjekttype={vegobjekttype}
                            farge={farge}
                        />
                        <div className="c-hva-info__content">
                            <h3 className="c-hva-info__title">
                                <span>{vegobjekttype.navn}</span>
                                <span className={'c-hva-info__id'}>
                                    {vegobjekttype.id}
                                </span>
                            </h3>
                            <div className="c-hva-info__actions">
                                <Categorize
                                    vegobjekttype={vegobjekttype}
                                    vegobjekttypeData={vegobjekttypeData}
                                />
                                <div className="c-advanced-filter__toggle-group">
                                    <button
                                        type="button"
                                        className="c-hva-info__button c-advanced-filter__toggle-button"
                                        onClick={
                                            isAdvancedFilterOpen
                                                ? this.clearAdvancedFilter
                                                : this.openAdvancedFilter
                                        }
                                        aria-label={`${filterToggleLabel} for ${vegobjekttype.navn}`}
                                    >
                                        <i
                                            className="material-icons c-advanced-filter__toggle-icon"
                                            aria-hidden="true"
                                        >
                                            {filterToggleIcon}
                                        </i>
                                        <span className="c-advanced-filter__toggle-label">
                                            {filterToggleLabel}
                                        </span>
                                    </button>
                                    <InfoBox
                                        className="c-advanced-filter__toggle-info"
                                        portalClassName="c-advanced-filter__tutorial-infobox"
                                        triggerAriaLabel="Vis forklaring av avansert filtrering"
                                    >
                                        <AdvancedFilterTutorial />
                                    </InfoBox>
                                </div>
                            </div>
                        </div>
                        <button
                            className="c-hva-info__remove-button"
                            type="button"
                            onClick={() => {
                                this.props.doRemoveRoadObjectType(vegobjekttypeData);
                            }}
                            aria-label={`Fjern vegobjekttype ${vegobjekttype.navn} fra søk`}
                        >
                            <i className="o-icon-remove c-hva-info__remove-icon" />
                        </button>
                    </div>
                    {isAdvancedFilterOpen && (
                        <AdvancedFilterBuilder
                            vegobjekttypeData={vegobjekttypeData}
                            egenskapstyper={vegobjekttype.egenskapstyper}
                        />
                    )}
                    {vegobjekttypeData.error && (
                        <section className="c-what-error">
                            <i className="icon-exclamation-triangle c-what-error__icon" />
                            {this.formatError(vegobjekttypeData.error)}
                        </section>
                    )}
                    {!vegobjekttypeData.error && !hasCategoryResults && (
                        <Accordion
                            id={'dropdown_' + vegobjekttype?.id.toString()}
                            className="c-filtere__section-bottom"
                            ariaLabel={`Vis liste av ${vegobjekttype.navn} objekter`}
                            header={this.getResultHeader()}
                            headerTrailing={
                                showAdvancedFilterWarning
                                    ? this.renderAdvancedFilterWarningIcon()
                                    : null
                            }
                        >
                            {result && <RoadRefOrAreaSummary results={result} />}
                            <DownloadResultsBox votState={vegobjekttypeData} />
                        </Accordion>
                    )}
                    {!vegobjekttypeData.error && hasCategoryResults && (
                        <>
                            <div className="c-filtere__section-bottom">
                                {vegobjekttypeData.loading && (
                                    <div className="c-hva-info__statistics-row c-hva-info__statistics-row--all-categories">
                                        <Loading
                                            className="c-hva-info__statistics"
                                            type="text"
                                        />
                                    </div>
                                )}
                                {!vegobjekttypeData.loading && result && (
                                    <AllCategoriesHitStatistics
                                        vegobjekttype={vegobjekttype}
                                        result={result}
                                        showAdvancedFilterWarning={
                                            showAdvancedFilterWarning
                                        }
                                    />
                                )}
                            </div>
                            <CategoryResultList
                                vegobjekttype={vegobjekttype}
                                vegobjekttypeData={vegobjekttypeData}
                                categoryStates={categoryStates}
                                categoryName={category.navn}
                                onOpenColorMenu={this.openCategoryColorMenu}
                            />
                            {activeColorCategory && (
                                <ColorMenu
                                    title={`Farge for ${activeColorCategory.toString()}`}
                                    currentColor={activeColorCategory.color}
                                    anchorPoint={this.state.categoryColorMenuAnchor}
                                    isOpen={this.state.isCategoryColorMenuOpen}
                                    onClose={this.closeCategoryColorMenu}
                                    actionRows={[
                                        {
                                            title: `Kategori: ${activeColorCategory.toString()}`,
                                            icon: (
                                                <i
                                                    className="o-icon-category c-color-menu__category-icon"
                                                    aria-hidden="true"
                                                />
                                            ),
                                            actions: this.getCategoryColorActions(
                                                vegobjekttypeData,
                                                activeColorCategory
                                            ),
                                        },
                                    ]}
                                />
                            )}
                        </>
                    )}
                </li>
            </>
        );
    }
    renderAdvancedFilterWarningIcon = () => {
        return <AdvancedFilterWarningIcon />;
    };
    formatError = (error: NonNullable<VegobjekttypeStateError>) => {
        const messages: Record<NonNullable<VegobjekttypeStateError>, string> = {
            auth: this.props.isLoggedIn
                ? 'Du har ikke tilgang til å se denne informasjonen.'
                : 'Data er skjermet. Innlogging og gyldig tilgang kreves.',
            network: 'Det har oppstått en nettverksfeil ved innlasting av data',
            api: 'Kan ikke hente data, ugyldig spørring',
            unknown: 'Ukjent feil har oppstått ved innlasting.',
            'not-indexed': 'Ikke tilgjengelig i Vegkart',
            'polygon-limit':
                'For mange vegobjekter i polygonsøk. Fjern polygonet for å se resultater.',
        };
        return messages[error] || messages['unknown'];
    };
    getResultHeader = () => {
        const { vegobjekttypeData, vegobjekttype } = this.props;
        const { result } = vegobjekttypeData;
        return (
            <>
                {vegobjekttypeData.loading && (
                    <Loading className="c-hva-info__statistics" type="text" />
                )}
                {!vegobjekttypeData.loading && result && (
                    <HitStatistics vegobjekttype={vegobjekttype} result={result} />
                )}
            </>
        );
    };
}

export const VegobjekttypeContainer = connect(
    null,
    mapDispatchToProps
)(VegobjekttypeContainerComponent);
